import React, { useEffect, useMemo, useState } from 'react';
import moment from 'moment';
import { MobileDateRangePicker } from '@mui/lab';
import { BarChart } from '@mui/x-charts/BarChart';
import { LineChart } from '@mui/x-charts/LineChart';
import {
  Alert,
  Box,
  Card,
  Container,
  Grid,
  Skeleton,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { endOfDay, format, startOfDay } from 'date-fns';
import Page from '../../../components/Page';
import HeaderBreadcrumbs from '../../../components/HeaderBreadcrumbs';
import DataGridTable from '../../../components/table/DataGridTable';
import Iconify from '../../../components/Iconify';
import useSettings from '../../../hooks/useSettings';
import { PATH_DASHBOARD } from '../../../routes/paths';
import { dispatch, useSelector } from '../../../redux/store';
import { getSalesMenuDashboard } from '../../../redux/slices/homeRedux/homeRedux';
import { fCurrency } from '../../../utils/formatNumber';

const SummaryCard = ({ title, value, icon, color }) => (
  <Card sx={{ p: 3, height: '100%' }}>
    <Stack direction='row' alignItems='center' justifyContent='space-between'>
      <Box>
        <Typography variant='h3'>{value}</Typography>
        <Typography variant='subtitle2' sx={{ color: 'text.secondary', mt: 1 }}>
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
          width: 56,
          height: 56,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          borderRadius: '50%',
          color,
          bgcolor: `${color}18`,
        }}
      >
        <Iconify icon={icon} width={28} height={28} />
      </Box>
    </Stack>
  </Card>
);

const ChartCard = ({ title, children }) => (
  <Card sx={{ p: 3, height: '100%' }}>
    <Typography variant='h6' sx={{ mb: 2 }}>
      {title}
    </Typography>
    {children}
  </Card>
);

const EmptyChart = () => (
  <Box sx={{ height: 300, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
    <Typography variant='body2' sx={{ color: 'text.secondary' }}>
      No sales data for the selected filters
    </Typography>
  </Box>
);

const SalesMenuDashboard = () => {
  const { themeStretch } = useSettings();
  const [openPicker, setOpenPicker] = useState(false);
  const [dateRange, setDateRange] = useState([startOfDay(new Date()), endOfDay(new Date())]);
  const [rowSelection, setRowSelection] = useState({});
  const { selectedLocation } = useSelector((state) => state.location);
  const { salesMenuDashboardData, salesMenuDashboardLoading, salesMenuDashboardError } = useSelector((state) => state.home);

  useEffect(() => {
    if (!dateRange[0] || !dateRange[1]) return;
    dispatch(
      getSalesMenuDashboard({
        fromDate: moment(dateRange[0]).startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        toDate: moment(dateRange[1]).endOf('day').format('YYYY-MM-DD HH:mm:ss'),
        locationId: selectedLocation.id,
      })
    );
  }, [dateRange, selectedLocation?.id]);

  const formatDateRange = (value) => {
    if (!value[0] || !value[1]) return '';
    return `${format(value[0], 'dd/MM/yyyy')} - ${format(value[1], 'dd/MM/yyyy')}`;
  };

  const summary = salesMenuDashboardData?.summary || {};
  const salesTrend = salesMenuDashboardData?.salesTrend || [];
  const categoryPerformance = salesMenuDashboardData?.categoryPerformance || [];
  const menuItems = salesMenuDashboardData?.menuItems || [];
  const topMenuItems = menuItems.slice(0, 10);

  const tableColumns = useMemo(
    () => [
      { accessorKey: 'name', header: 'Menu Item' },
      { accessorKey: 'category', header: 'Category' },
      { accessorKey: 'quantity', header: 'Quantity Sold' },
      {
        accessorKey: 'revenue',
        header: 'Revenue',
        Cell: ({ cell }) => fCurrency(cell.getValue()),
      },
      {
        accessorKey: 'averageSellingPrice',
        header: 'Average Selling Price',
        Cell: ({ cell }) => fCurrency(cell.getValue()),
      },
    ],
    []
  );

  return (
    <Page title='Sales & Menu Dashboard'>
      <Container maxWidth={themeStretch ? false : 'xl'}>
        <HeaderBreadcrumbs
          heading='Sales & Menu Dashboard'
          links={[
            { name: 'Dashboard', href: PATH_DASHBOARD.root },
            { name: 'Reports', href: PATH_DASHBOARD.report.root },
            { name: 'Sales & Menu' },
          ]}
        />

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <MobileDateRangePicker
              open={openPicker}
              onClose={() => setOpenPicker(false)}
              onOpen={() => setOpenPicker(true)}
              value={dateRange}
              onChange={() => {}}
              onAccept={(value) => setDateRange(value)}
              renderInput={({ inputRef, inputProps, InputProps }) => (
                <TextField
                  ref={inputRef}
                  {...inputProps}
                  InputProps={InputProps}
                  fullWidth
                  label='Date Range'
                  value={formatDateRange(dateRange)}
                  onClick={() => setOpenPicker(true)}
                  inputProps={{ ...inputProps, readOnly: true }}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={6} />

          {salesMenuDashboardError && (
            <Grid item xs={12}>
              <Alert severity='error'>{salesMenuDashboardError}</Alert>
            </Grid>
          )}

          {[
            ['Total Revenue', fCurrency(summary.totalRevenue || 0), 'solar:wallet-money-bold', '#00AB55'],
            ['Quantity Sold', Number(summary.quantitySold || 0).toLocaleString(), 'solar:cart-large-2-bold', '#1890FF'],
            ['Guest Orders', Number(summary.orderCount || 0).toLocaleString(), 'solar:bill-list-bold', '#FFC107'],
            ['Average Order Value', fCurrency(summary.averageOrderValue || 0), 'solar:chart-2-bold', '#8E33FF'],
          ].map(([title, value, icon, color]) => (
            <Grid item xs={12} sm={6} lg={3} key={title}>
              {salesMenuDashboardLoading ? <Skeleton variant='rounded' height={124} /> : <SummaryCard title={title} value={value} icon={icon} color={color} />}
            </Grid>
          ))}

          <Grid item xs={12} lg={8}>
            <ChartCard title='Daily Sales Trend'>
              {salesMenuDashboardLoading ? (
                <Skeleton variant='rounded' height={300} />
              ) : salesTrend.length > 0 ? (
                <LineChart
                  height={300}
                  xAxis={[{ scaleType: 'point', data: salesTrend.map((item) => moment(item.date).format('DD MMM')) }]}
                  series={[{ data: salesTrend.map((item) => item.revenue), label: 'Revenue', area: true }]}
                />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </Grid>
          <Grid item xs={12} lg={4}>
            <ChartCard title='Revenue by Category'>
              {salesMenuDashboardLoading ? (
                <Skeleton variant='rounded' height={300} />
              ) : categoryPerformance.length > 0 ? (
                <BarChart
                  height={300}
                  xAxis={[{ scaleType: 'band', data: categoryPerformance.map((item) => item.category) }]}
                  series={[{ data: categoryPerformance.map((item) => item.revenue), label: 'Revenue' }]}
                />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <ChartCard title='Top 10 Menu Items by Revenue'>
              {salesMenuDashboardLoading ? (
                <Skeleton variant='rounded' height={320} />
              ) : topMenuItems.length > 0 ? (
                <BarChart
                  height={320}
                  xAxis={[{ scaleType: 'band', data: topMenuItems.map((item) => item.name) }]}
                  series={[{ data: topMenuItems.map((item) => item.revenue), label: 'Revenue' }]}
                />
              ) : (
                <EmptyChart />
              )}
            </ChartCard>
          </Grid>
          <Grid item xs={12}>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Menu Performance
            </Typography>
            <DataGridTable
              name='Menu Performance'
              data={menuItems}
              column={tableColumns}
              isLoading={salesMenuDashboardLoading}
              rowSelection={rowSelection}
              setRowSelection={setRowSelection}
              enableRowSelection={false}
              enableRowActions={false}
              enablePinning={false}
              isRowClickable={false}
            />
          </Grid>
        </Grid>
      </Container>
    </Page>
  );
};

export default SalesMenuDashboard;
